import { useQuery } from "@apollo/client";
import { useEffect } from "react";
import { useIntl } from "react-intl";

import CircleButton from "@components/buttons/CircleButton";
import styles from "@components/FileResource.module.css";
import TextWithIcon from "@components/text/TextWithIcon";
import BodyCopy from "@components/typography/BodyCopy";
import useDownloadFileResource from "@hooks/useDownloadFileResource";
import {
  FileResourceFragment,
  GetAttachmentDocument,
  GetAttachmentQuery,
  GetAttachmentQueryVariables
} from "@typing/Generated";
import { getFileImage } from "@utils/fileUtils";
import { imageUrl } from "@utils/imageUtils";
import { getFileResourceTitle } from "@utils/modelUtils";

type AttachmentType = NonNullable<FileResourceFragment["attachment"]>;

type Props = {
  fileResource: FileResourceFragment;
  textColor?: "white";
};

const FileResource = ({ fileResource, textColor }: Props) => {
  const intl = useIntl();
  const attachment = fileResource.attachment as AttachmentType;

  const getAttachmentQuery = useQuery<GetAttachmentQuery, GetAttachmentQueryVariables>(GetAttachmentDocument, {
    skip: !attachment.scanning,
    variables: { id: attachment.id }
  });

  const handleDownload = useDownloadFileResource(fileResource);

  useEffect(() => {
    if (attachment.scanning) {
      getAttachmentQuery.startPolling(2500);
    } else {
      getAttachmentQuery.stopPolling();
    }

    return () => {
      getAttachmentQuery.stopPolling();
    };
  }, [attachment.scanning, getAttachmentQuery]);

  return (
    <div>
      {attachment.scanning ? (
        <BodyCopy color={textColor ?? "gray"}>
          <TextWithIcon icon="spinner" iconFirst text={intl.formatMessage({ id: "dictionary.scanning" })} />
        </BodyCopy>
      ) : (
        <div className={styles.fileResource}>
          <div className={styles.imgAndTitle}>
            <img
              aria-label={intl.formatMessage({ id: "accessibility.ariaLabels.file" })}
              className={styles.fileResourceImg}
              src={imageUrl(getFileImage(fileResource))}
            />
            <BodyCopy color={textColor ?? "gray"}>{getFileResourceTitle(fileResource, intl)}</BodyCopy>
          </div>
          <CircleButton
            ariaLabelKey="accessibility.ariaLabels.download"
            color={textColor}
            fill="clear"
            icon="download"
            onClick={handleDownload}
          />
        </div>
      )}
    </div>
  );
};

export default FileResource;
